/**
 * Step 2: Input
 *
 * Document mode: FileDropzone for PDF/DOC/DOCX upload
 * URL mode: TextField for URL input
 */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Button, TextField } from "@mui/material";
import FileDropzone from "../FileDropzone";
import type { JobImportWizardState } from "./JobImportWizard";

const JOB_DOC_FORMATS = {
  "application/pdf": [".pdf"],
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
};

interface Props {
  wizardState: JobImportWizardState;
  onNext: () => void;
  onBack: () => void;
  updateState: (updates: Partial<JobImportWizardState>) => void;
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const Step2_Input: React.FC<Props> = ({
  wizardState,
  onNext,
  onBack,
  updateState,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const isUrl = wizardState.importMethod === "url";

  // Document mode state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // URL mode state
  const [url, setUrl] = useState(wizardState.url || "");
  const [urlError, setUrlError] = useState("");

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    updateState({
      uploadedFile: file,
      importSourceRef: file.name,
    });
  };

  const validateUrl = (value: string): boolean => {
    try {
      const parsed = new URL(value);
      if (!["http:", "https:"].includes(parsed.protocol)) {
        setUrlError(
          t(
            "jobImport.step2.urlErrorProtocol",
            "Only HTTP and HTTPS URLs are allowed.",
          ),
        );
        return false;
      }
      setUrlError("");
      return true;
    } catch {
      setUrlError(
        t("jobImport.step2.urlErrorInvalid", "Please enter a valid URL."),
      );
      return false;
    }
  };

  const handleNext = () => {
    if (isUrl) {
      if (url.trim() && validateUrl(url.trim())) {
        updateState({ url: url.trim(), importSourceRef: url.trim() });
        onNext();
      }
    } else if (selectedFile) {
      onNext();
    }
  };

  const canProceed = isUrl ? url.trim().length > 0 : !!selectedFile;

  if (isUrl) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          {t("jobImport.step2.urlTitle", "Enter Job Posting URL")}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {t(
            "jobImport.step2.urlSubtitle",
            "Paste the URL of a job posting page. We'll fetch and extract the job details.",
          )}
        </Typography>

        <TextField
          fullWidth
          label={t("jobImport.step2.urlLabel", "Job Posting URL")}
          placeholder="https://example.com/jobs/senior-developer"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            if (urlError) setUrlError("");
          }}
          error={!!urlError}
          helperText={urlError}
          sx={{ mb: 2 }}
          autoFocus
        />

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button onClick={onBack} variant="outlined">
            {t("common:actions.back", "Back")}
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canProceed}
            data-testid="next-button"
          >
            {t("jobImport.step2.nextParse", "Parse Job")}
          </Button>
        </Box>
      </Box>
    );
  }

  // Document upload mode
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t("jobImport.step2.documentTitle", "Upload Job Description")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t(
          "jobImport.step2.documentSubtitle",
          "Upload a PDF, DOC, or DOCX file containing the job description.",
        )}
      </Typography>

      <FileDropzone
        onFileSelect={handleFileSelect}
        acceptedFormats={JOB_DOC_FORMATS}
        maxSizeMB={10}
      />

      {selectedFile && (
        <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
          {t("jobImport.step2.selected", {
            defaultValue: "Selected: {{name}} ({{size}})",
            name: selectedFile.name,
            size: formatFileSize(selectedFile.size),
          })}
        </Typography>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
        <Button onClick={onBack} variant="outlined">
          {t("common:actions.back", "Back")}
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!selectedFile}
          data-testid="next-button"
        >
          {t("jobImport.step2.nextParse", "Parse Job")}
        </Button>
      </Box>
    </Box>
  );
};

export default Step2_Input;
